import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, FileText, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { instructors as instructorsApi, payroll as payrollApi, rewards as rewardsApi } from '@/api/endpoints'
import { Alert, EmptyState, Field, Select, Spinner } from '@/components/ui'
import Pagination from '@/components/ui/Pagination'
import { formatDate, formatMoney, minutesToHours } from '@/lib/utils'
import { useAuth } from '@/store/auth'

import InstructorRates from './InstructorRates'
import { CreatePayslipDialog, PayslipList } from './Payslips'
import RangePicker from './RangePicker'
import { useRewardRange, useTaughtOnly } from './useRewardRange'
import { rewardTypeLabel } from '@/features/rewards/typeLabel'
import { CenterFilter, useCenterFilter } from '@/features/centers/useCenters'

/** InstructorPresenter::actionReward - one instructor's payout sheet.
 *
 *  Za zvolené obdobie ukazuje sumu **na výplatu** (nevyplatené a ešte nie na
 *  výplatnom liste) a z nej vytvorí **výplatný list** - zo všetkých odmien na
 *  výplatu, alebo len z vybraných riadkov (`Payslips.tsx`, `sql/039`). */
export default function InstructorRewardsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const instructorId = Number.parseInt(id ?? '0', 10)
  const readOnly = !useAuth((s) => s.user)?.can_write

  const [range, setRange] = useRewardRange()
  const [taughtOnly, setTaughtOnly] = useTaughtOnly()
  const [search] = useSearchParams()
  const { from, to } = range

  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(
    () => Number.parseInt(localStorage.getItem('l2s:rewardPageSize') ?? '', 10) || 10,
  )

  // Strediská (`sql/040`) - len odmeny z blokov vybraných stredísk.
  const [centerIds] = useCenterFilter()
  const { data, isLoading } = useQuery({
    queryKey: ['instructor-rewards', instructorId, from, to, page, pageSize, taughtOnly, centerIds.join(',')],
    queryFn: () =>
      instructorsApi.rewards(
        instructorId,
        from || undefined,
        to || undefined,
        page,
        pageSize,
        taughtOnly,
        centerIds,
      ),
    enabled: Boolean(instructorId),
    // Without this the table blanks out on every page step.
    placeholderData: (previous) => previous,
  })

  // Čísla výplatných listov pri riadkoch (odmena na liste sa nemení).
  const { data: payslips } = useQuery({
    queryKey: ['payslips', instructorId],
    queryFn: () => payrollApi.payslips(instructorId),
    enabled: Boolean(instructorId),
  })
  const payslipById = new Map((payslips ?? []).map((p) => [p.id, p]))

  // The server clamps an out-of-range page (a shorter period can drop pages
  // from under us); mirror its answer so the controls stay honest.
  useEffect(() => {
    if (data && data.page !== page) setPage(data.page)
  }, [data, page])

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['instructor-rewards'] })
    void qc.invalidateQueries({ queryKey: ['rewards-overview'] })
  }

  const updateReward = useMutation({
    mutationFn: ({ rewardId, value }: { rewardId: number; value: string }) =>
      rewardsApi.update(rewardId, { reward: value }),
    onSuccess: invalidate,
  })
  const markPaid = useMutation({
    mutationFn: (ids: number[]) => rewardsApi.markPaid(ids, true),
    onSuccess: () => {
      setSelected(new Set())
      invalidate()
    },
  })
  const recalculate = useMutation({
    mutationFn: () => rewardsApi.recalculate({ from: from || undefined, to: to || undefined }),
    onSuccess: invalidate,
  })

  function toggle(rewardId: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(rewardId)) next.delete(rewardId)
      else next.add(rewardId)
      return next
    })
  }

  // Only unpaid rows can be selected - marking an already paid one again is a
  // no-op that would just inflate the counter on the button.
  const selectableIds = (data?.items ?? []).filter((r) => !r.paid && !r.payslip_id).map((r) => r.id)
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id))
  const selectedTotal = (data?.items ?? [])
    .filter((r) => selected.has(r.id))
    .reduce((sum, r) => sum + Number.parseFloat(r.reward || '0'), 0)

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(selectableIds))
  }

  if (isLoading || !data) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Link to={{ pathname: '/rewards', search: search.toString() ? `?${search}` : '' }} className="btn-ghost mb-1 p-1.5">
          <ArrowLeft size={16} />
        </Link>
        <h1 className="mb-1 text-lg font-semibold">{data.instructor.full_name}</h1>

        {/* Changing the period clears the selection - marking rows that have
            scrolled out of view as paid would be a nasty surprise. */}
        <RangePicker
          value={range}
          onChange={(next) => {
            setRange(next)
            setSelected(new Set())
            setPage(1)
          }}
        />

        <CenterFilter className="w-52" />
        <Field label={t('units.countWhat')} className="w-56 max-w-full">
          <Select
            value={taughtOnly ? 'taught' : 'confirmed'}
            onChange={(e) => {
              setTaughtOnly(e.target.value === 'taught')
              setSelected(new Set())
              setPage(1)
            }}
          >
            <option value="confirmed">{t('units.countConfirmed')}</option>
            <option value="taught">{t('units.countTaught')}</option>
          </Select>
        </Field>

        {!readOnly ? (
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => recalculate.mutate()}
              disabled={recalculate.isPending}
            >
              <RefreshCw size={15} className={recalculate.isPending ? 'animate-spin' : ''} />
              {t('rewards.recalculate')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={selected.size === 0 || markPaid.isPending}
              onClick={() => markPaid.mutate([...selected])}
            >
              <Check size={15} />
              {t('rewards.markPaid')}
              {selected.size > 0 ? ` (${selected.size} · ${formatMoney(selectedTotal)})` : ''}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!Number.parseFloat(data.summary.to_pay ?? '0') && selected.size === 0}
              onClick={() => setCreating(true)}
            >
              <FileText size={15} />
              {t('payroll.create')}
            </button>
          </div>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-5">
        <Stat label={t('global.total')} value={formatMoney(data.summary.total)} />
        <Stat label={t('rewards.paid')} value={formatMoney(data.summary.paid)} />
        <Stat label={t('rewards.unpaid')} value={formatMoney(data.summary.unpaid)} />
        <Stat
          label={t('payroll.toPay')}
          value={formatMoney(data.summary.to_pay ?? data.summary.unpaid)}
          highlight
        />
        <Stat
          label={t('statistics.totalHours')}
          value={minutesToHours(data.lessons.total)}
        />
      </div>

      {/* Aktuálna hodinová sadzba inštruktora a jej história (graf). */}
      <InstructorRates instructorId={instructorId} readOnly={readOnly} />

      {!readOnly ? <Alert variant="info">{t('rewards.editHint')}</Alert> : null}

      <div className="card overflow-x-auto">
        <table className="table-base">
          <thead>
            <tr>
              <th className="w-8">
                {!readOnly && selectableIds.length > 0 ? (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    // `indeterminate` is a DOM property, not an attribute -
                    // React cannot set it declaratively.
                    ref={(el) => {
                      if (el) el.indeterminate = selected.size > 0 && !allSelected
                    }}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-slate-300 text-brand-500"
                    title={t('rewards.selectAllOnPage')}
                    aria-label={t('rewards.selectAllOnPage')}
                  />
                ) : null}
              </th>
              <th>{t('reservation.date')}</th>
              <th>{t('rewards.type')}</th>
              <th>{t('reservation.client')}</th>
              <th className="text-right">{t('global.minutes')}</th>
              <th className="text-right">{t('rewards.amount')}</th>
              <th>{t('rewards.paid')}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((reward) => (
              <tr key={reward.id}>
                <td>
                  {!readOnly && !reward.paid && !reward.payslip_id ? (
                    <input
                      type="checkbox"
                      checked={selected.has(reward.id)}
                      onChange={() => toggle(reward.id)}
                      className="h-4 w-4 rounded border-slate-300 text-brand-500"
                    />
                  ) : null}
                </td>
                <td className="whitespace-nowrap">{formatDate(reward.reservation_start)}</td>
                <td>
                  {/* Ručná odmena (typ 999) s vlastným názvom - ten je výstižnejší než „ručná úprava“. */}
                  {reward.type_id === 999 && reward.description
                    ? reward.description
                    : rewardTypeLabel(t, reward)}
                </td>
                <td className="text-slate-500">{reward.client_name || '—'}</td>
                <td className="text-right tabular-nums text-slate-500">{reward.number}</td>
                <td className="text-right tabular-nums">
                  {editing === reward.id ? (
                    <input
                      type="number"
                      step="0.01"
                      autoFocus
                      className="input w-24 text-right"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={() => {
                        if (draft && draft !== reward.reward) {
                          updateReward.mutate({ rewardId: reward.id, value: draft })
                        }
                        setEditing(null)
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                    />
                  ) : (
                    <button
                      type="button"
                      disabled={readOnly || reward.paid || Boolean(reward.payslip_id)}
                      className="hover:underline disabled:no-underline"
                      onClick={() => {
                        setEditing(reward.id)
                        setDraft(reward.reward)
                      }}
                    >
                      {formatMoney(reward.reward)}
                    </button>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  {reward.paid ? (
                    <Check size={14} className="inline text-green-600" />
                  ) : (
                    <span className="text-xs text-amber-600">{t('rewards.unpaid')}</span>
                  )}
                  {reward.payslip_id ? (
                    <a
                      href={payrollApi.pdfUrl(reward.payslip_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t('payroll.onPayslip')}
                      className="ml-2 rounded border border-brand-200 px-1 py-0.5 text-[11px] text-brand-700 hover:bg-brand-50"
                    >
                      {payslipById.get(reward.payslip_id)?.number ?? t('payroll.onPayslip')}
                    </a>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.items.length === 0 ? <EmptyState>{t('app.empty')}</EmptyState> : null}

        <Pagination
          page={data.page}
          pages={data.pages}
          pageSize={data.page_size}
          count={data.count}
          onPageChange={(next) => {
            setPage(next)
            // The selection only ever covers rows you can see.
            setSelected(new Set())
          }}
          onPageSizeChange={(size) => {
            setPageSize(size)
            localStorage.setItem('l2s:rewardPageSize', String(size))
            setPage(1)
            setSelected(new Set())
          }}
        />
      </div>

      <PayslipList instructorId={instructorId} readOnly={readOnly} />

      {creating ? (
        <CreatePayslipDialog
          instructorId={instructorId}
          periodFrom={String(data.from).slice(0, 10)}
          periodTo={String(data.to).slice(0, 10)}
          taughtOnly={taughtOnly}
          centerIds={centerIds}
          toPay={Number.parseFloat(data.summary.to_pay ?? '0')}
          toPayCount={data.summary.to_pay_count ?? 0}
          selectedIds={[...selected]}
          selectedTotal={selectedTotal}
          onClose={() => {
            setCreating(false)
            setSelected(new Set())
          }}
        />
      ) : null}

      {Object.keys(data.lessons.lessons).length > 0 ? (
        <div className="card p-3">
          <h2 className="mb-2 text-xs font-semibold uppercase text-slate-500">
            {t('rewards.lessonsBreakdown')}
          </h2>
          <div className="flex flex-wrap gap-2 text-sm">
            {Object.entries(data.lessons.lessons).map(([minutes, count]) => (
              <span key={minutes} className="rounded bg-slate-100 px-2 py-1">
                {minutes} {t('global.minutes')} × <strong>{count}</strong>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string
  value: string
  highlight?: boolean
}) {
  return (
    <div className="card p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={highlight ? 'text-lg font-semibold text-amber-600' : 'text-lg font-semibold'}>
        {value}
      </div>
    </div>
  )
}
